/**
 * The first screen an authenticated user with no local business sees.
 *
 * WHY THIS EXISTS: Dexie is the source of truth, so an empty local database
 * means "no business" — even when the user has a fully synced company in
 * Supabase. Before this gate, such a device could only CREATE a new business,
 * minting a new id and stranding the real one in the cloud: the exact gap found
 * in the recovery test. So this gate offers THREE paths and only decides which to
 * offer by asking the cloud, never by guessing.
 *
 * WHAT IT DOES NOT DO: it does not touch Supabase RPCs, Dexie writes, or sync
 * ordering directly. It calls `listRestorableCloudBusinesses` and
 * `restoreCloudBusiness`, which own the whole restore sequence — snapshot,
 * one-transaction apply, cursor, device registration, incremental catch-up.
 * "Create New Business" is the existing Settings → Business Profile screen; this
 * gate only navigates there. No second sync path, no second creation path.
 *
 * OFFLINE EMPTY DEVICE: with no local business and no way to reach the cloud,
 * discovery cannot run. The gate shows all recovery options with cloud restore
 * disabled and an explanatory message, NOT "Create New Business" as the only option,
 * because that would let an offline user mint a new company and diverge from the
 * one they already own.
 */
import { useCallback, useState } from 'react';
import {
  Building2,
  CloudDownload,
  HardDrive,
  LogOut,
  Plus,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@ui/components/Button';
import { ErrorState } from '@ui/components/States';
import { toast } from '@ui/components/Toaster';
import { listRestorableCloudBusinesses, restoreCloudBusiness } from '@data/sync/cloudRestore';
import { gateRestoreMessage } from './syncRepair';
import { getDb } from '@data/db/database';
import { readDiskBackup, restoreFromDiskBackup } from '@data/backup/localBackupEngine';
import { resolveDiskBackupTarget } from '@data/backup/resolveDiskBackupTarget';
import {
  BUSINESSES_QUERY_ROOT,
  INVENTORY_QUERY_ROOTS,
  GATE_QUERY_KEY,
} from '@features/documents/useDocuments';
import { useAuth } from '@platform/auth/authContext';

function messageFor(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

function cloudAvailabilityMessage(error: unknown | null): string {
  if (!error) return '';
  const msg = messageFor(error);
  if (msg.includes('NOT_AUTHENTICATED') || msg.includes('Sign in')) {
    return 'Sign in to restore a synced business from the cloud.';
  }
  if (msg.includes('UNREACHABLE') || msg.includes('Could not reach') || msg.includes('offline')) {
    return 'Cannot reach the sync service. Connect to the internet to restore from cloud.';
  }
  if (msg.includes('FORBIDDEN') || msg.includes('Could not fetch')) {
    return 'Unable to access cloud data. Check your connection and try again.';
  }
  return `Cloud restore unavailable: ${msg}`;
}

export function BusinessGate() {
  const queryClient = useQueryClient();
  const db = getDb();
  const { signOut } = useAuth();

  const [restoring, setRestoring] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);
  const [restoringLocal, setRestoringLocal] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  /**
   * Recover from this device's own automatic disk backup.
   *
   * This gate only renders when the device holds NO business, so restoring over
   * it cannot destroy local data — which is why the explicit click on "Restore
   * from Local Backup" is the confirmation `restoreBackup` demands. On success
   * `restoreFromDiskBackup` has already initialised the business session, and we
   * invalidate every cached list (the restore wrote underneath the whole app) and
   * navigate into the workspace. Cloud discovery is untouched: disk recovery must
   * work with no network at all.
   */
  const handleRestoreLocal = useCallback(async () => {
    setRestoringLocal(true);
    setRestoreError(null);
    try {
      const { target, durable } = resolveDiskBackupTarget();
      // Restore is "point me at a backup folder". A fresh device has NO stored
      // folder handle — it is only saved once backup is enabled — so reading
      // straight from disk would fail with PERMISSION_DENIED ("No backup folder
      // is available"). Open the folder picker FIRST, before any await, because
      // the File System Access API requires the click's user activation to still
      // be live when showDirectoryPicker runs. `durable` is false only for the
      // in-memory target (a browser with no File System Access and no Electron
      // bridge), where there is nothing on disk to restore from.
      if (!durable) {
        throw new Error(
          'Restoring from a local backup folder is not supported here. Use Chrome or Edge, or the desktop app.',
        );
      }
      await target.pickFolder();
      const read = await readDiskBackup(target);
      if (!read.ok) {
        throw new Error(read.message ?? 'That folder does not contain a DNR backup.');
      }
      const result = await restoreFromDiskBackup(target, db, {
        confirmed: true,
        now: new Date().toISOString(),
      });
      // Invalidate the exact gate query key so BusinessGatekeeper re-checks immediately
      await queryClient.invalidateQueries({ queryKey: GATE_QUERY_KEY, exact: true });
      for (const root of INVENTORY_QUERY_ROOTS) {
        await queryClient.invalidateQueries({ queryKey: root });
      }
      await queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_ROOT });
      toast({
        title: 'Business restored',
        description: result.activeBusinessId
          ? 'Your business was restored from the local backup on this device.'
          : 'Restored from the local backup on this device.',
        tone: 'success',
      });
      window.location.hash = '#/settings/business';
    } catch (e) {
      setRestoreError(messageFor(e));
    } finally {
      setRestoringLocal(false);
    }
  }, [db, queryClient]);

  const handleLogout = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOut();
    } catch {
      setRestoreError('Unable to sign out. Please try again.');
    } finally {
      setSigningOut(false);
    }
  }, [signOut]);

  /**
   * Ask the cloud what this account has synced — as a query, not an effect that
   * sets state on mount. Discovery is server state: `data` is the answer (an
   * empty array means "nothing to restore", distinct from "not asked yet" which
   * is `isLoading`), and `refetch` is the retry. `retry: false` so a transport
   * failure surfaces immediately rather than after three silent backoffs that
   * would look like a hang on an already-frustrated empty device.
   */
  const discovery = useQuery({
    queryKey: ['cloud', 'restorable-businesses'],
    queryFn: () => listRestorableCloudBusinesses(db),
    retry: false,
  });

  const handleRestore = useCallback(
    async (businessId: string) => {
      setRestoring(businessId);
      setRestoreError(null);
      try {
        const result = await restoreCloudBusiness(db, businessId);
        // The already-local branch reports a refused recovery as a VALUE, not an
        // exception — it must not reach the success path below or the gate would
        // announce a restore that did not happen.
        const outcome = gateRestoreMessage(result);
        // The restore wrote rows underneath the whole app, so every cached list
        // is now stale — invalidate wholesale, exactly as a company switch does.
        // A refused recovery may still have changed the outbox, so the sync card
        // is re-read either way.
        await queryClient.invalidateQueries({ queryKey: GATE_QUERY_KEY, exact: true });
        for (const root of INVENTORY_QUERY_ROOTS) {
          await queryClient.invalidateQueries({ queryKey: root });
        }
        await queryClient.invalidateQueries({ queryKey: BUSINESSES_QUERY_ROOT });
        toast({ title: outcome.title, description: outcome.description, tone: outcome.tone });
        if (!outcome.ok) {
          // Stay on the gate: the business is on this device, but nothing about
          // its cloud copy was established, and navigating away would present
          // that as a finished restore.
          setRestoreError(outcome.description);
          return;
        }
        window.location.hash = '#/sales/invoices';
      } catch (error) {
        setRestoreError(messageFor(error));
        toast({ title: messageFor(error), tone: 'danger' });
      } finally {
        setRestoring(null);
      }
    },
    [db, queryClient],
  );

  const cloudList = discovery.data ?? [];
  const hasCloud = cloudList.length > 0;
  const cloudUnavailable = discovery.isError || discovery.isLoading;
  const cloudErrorMessage = discovery.isError ? cloudAvailabilityMessage(discovery.error) : '';

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 p-6">
      <div className="w-full max-w-md">
        <h1 className="text-xl font-semibold text-slate-100">Welcome to DNR</h1>
        <p className="mt-1 text-sm text-slate-400">There is no business on this device yet.</p>

        {restoreError ? (
          <div className="mt-4">
            <ErrorState message={restoreError} onRetry={() => setRestoreError(null)} />
          </div>
        ) : null}

        {/* RESTORE FROM CLOUD. Always visible; disabled with explanation when unavailable. */}
        <div className="mt-6 rounded-lg border border-slate-800 bg-slate-900 p-4">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <CloudDownload className="h-4 w-4 text-brand-400" aria-hidden />
            Restore from Cloud
          </span>
          <p className="mt-1 text-xs text-slate-400">
            Bring back a business you have already synced to your DNR account.
          </p>
          {cloudUnavailable ? (
            <>
              <p className="mt-2 text-xs text-amber-300 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" aria-hidden />
                {cloudErrorMessage || 'Checking for synced businesses…'}
              </p>
              <Button
                block
                variant="secondary"
                disabled={discovery.isLoading || discovery.isFetching}
                loading={discovery.isLoading || discovery.isFetching}
                loadingText={discovery.isLoading ? 'Checking…' : 'Retrying…'}
                onClick={() => void discovery.refetch()}
              >
                {discovery.isLoading ? 'Checking…' : 'Retry Cloud Restore'}
              </Button>
            </>
          ) : hasCloud ? (
            <ul className="mt-3 flex flex-col gap-2" data-testid="restore-candidates">
              {cloudList.map((business) => (
                <li key={business.businessId}>
                  <Button
                    block
                    variant="secondary"
                    loading={restoring === business.businessId}
                    loadingText="Restoring…"
                    icon={<Building2 className="h-4 w-4" aria-hidden />}
                    onClick={() => void handleRestore(business.businessId)}
                  >
                    {business.displayName ?? business.businessId}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <>
              <p className="mt-2 text-xs text-slate-500">
                No synced businesses were found on this account.
              </p>
              {!cloudUnavailable && (
                <p className="mt-2 text-xs text-amber-300">
                  Businesses must be explicitly synced from another device first. If you have a
                  business on another device, open Settings → Sync & Cloud there and click "Enable
                  Cloud Sync" to upload it to this account.
                </p>
              )}
            </>
          )}
          {hasCloud && !cloudUnavailable && (
            <button
              type="button"
              onClick={() => void discovery.refetch()}
              disabled={discovery.isFetching}
              className="mt-3 inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200 disabled:opacity-50"
            >
              <RefreshCw className="h-3 w-3" aria-hidden />
              {discovery.isFetching ? 'Checking…' : 'Check again'}
            </button>
          )}
        </div>

        {/* RESTORE FROM DISK. Recovery from this device's own automatic backup. */}
        <div className="mt-3 rounded-lg border border-slate-800 bg-slate-900 p-4">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <HardDrive className="h-4 w-4 text-brand-400" aria-hidden />
            Restore from Local Backup
          </span>
          <p className="mt-1 text-xs text-slate-400">
            Bring back data from the automatic backup saved on this device — no internet needed.
            You&rsquo;ll be asked to choose the backup folder.
          </p>
          <div className="mt-3">
            <Button
              block
              variant="secondary"
              loading={restoringLocal}
              loadingText="Restoring…"
              onClick={() => void handleRestoreLocal()}
              data-testid="restore-local-backup"
            >
              Restore from Local Backup
            </Button>
          </div>
        </div>

        {/* CREATE. The existing first-run path; this gate only navigates to it. */}
        <a
          href="#/settings/business"
          className="mt-6 block rounded-lg border border-slate-800 bg-slate-900 p-4 text-left transition-colors hover:border-brand-600 hover:bg-slate-800"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-100">
            <Plus className="h-4 w-4 text-brand-400" aria-hidden />
            Create New Business
          </span>
          <span className="mt-1 block text-xs text-slate-400">
            Start a fresh set of books on this device.
          </span>
        </a>

        {/* LOGOUT. Switches accounts without losing local data. */}
        <button
          type="button"
          data-testid="business-gate-logout"
          onClick={() => void handleLogout()}
          disabled={signingOut}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:border-slate-700 hover:text-slate-100"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          {signingOut ? 'Signing out…' : 'Sign out and switch accounts'}
        </button>
      </div>
    </div>
  );
}
